import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useReducedMotion } from '@/hooks/useReducedMotion';

export interface AnimatedListItem {
  id?: string;
  label: string;
  title: string;
  body: string;
}

interface AnimatedListProps {
  items: AnimatedListItem[];
  className?: string;
  delay?: number;
}

export default function AnimatedList({ items, className, delay = 0 }: AnimatedListProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const prefersReducedMotion = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: delay,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: prefersReducedMotion ? 0 : 20,
      x: prefersReducedMotion ? 0 : -10,
    },
    show: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.5,
        ease: 'easeOut',
      },
    },
  };

  return (
    <motion.ol
      ref={ref}
      variants={prefersReducedMotion ? undefined : containerVariants}
      initial={prefersReducedMotion ? false : 'hidden'}
      animate={prefersReducedMotion || isInView ? 'show' : 'hidden'}
      className={cn('space-y-4', className)}
    >
      {items.map((item, index) => (
        <motion.li
          key={item.id || index}
          variants={prefersReducedMotion ? undefined : itemVariants}
          className="relative"
        >
          <div className="rounded-xl border border-slate-200 bg-white/80 p-4 md:p-5 shadow-sm hover:shadow-md transition-shadow duration-200 relative pl-6 md:pl-7">
            {/* Blue accent line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0000FF] rounded-l-xl" />
            
            {/* Content */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-[#0000FF]">{item.label}</span>
              </div>
              <h4 className="text-lg md:text-xl font-semibold text-foreground">{item.title}</h4>
              <p className="text-sm md:text-base text-slate-600 leading-relaxed">{item.body}</p>
            </div>
          </div>
        </motion.li>
      ))}
    </motion.ol>
  );
}

